import React, { useState, useEffect, useCallback, useRef } from "react";
import { Slide, Card, Icon, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";

/**
 * Componente de Notificação Automática de Atualização (Auto-Updater).
 * Detecta novas versões publicadas no servidor e oferece ao usuário
 * um botão para recarregar a aplicação de forma limpa (Ctrl + F5 automático).
 */
export default function UpdateNotifier() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const initialBuildTimeRef = useRef(null);
  const initialScriptsRef = useRef(new Set());

  // Extrai hashes dos scripts main.*.js carregados na inicialização
  useEffect(() => {
    const scripts = document.querySelectorAll("script[src]");
    scripts.forEach((s) => {
      const src = s.getAttribute("src") || "";
      if (src.includes("static/js/main.") || src.includes("main.")) {
        initialScriptsRef.current.add(src);
      }
    });

    // Lê o version.json inicial
    fetch(`/version.json?_init=${Date.now()}`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.buildTime) {
          initialBuildTimeRef.current = Number(data.buildTime);
        }
      })
      .catch(() => {});
  }, []);

  // Função principal de verificação contra o servidor
  const checkForUpdates = useCallback(async () => {
    if (dismissed || updateAvailable) return;

    try {
      // 1. Checagem via version.json
      const vRes = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache, no-store, must-revalidate", Pragma: "no-cache" }
      });

      if (vRes.ok) {
        const vData = await vRes.json();
        if (vData && vData.buildTime) {
          const remoteBuild = Number(vData.buildTime);
          if (initialBuildTimeRef.current && remoteBuild > initialBuildTimeRef.current) {
            console.log(`[UpdateNotifier] 🚀 Nova versão detectada no servidor! (Carregada: ${initialBuildTimeRef.current} | Servidor: ${remoteBuild})`);
            setUpdateAvailable(true);
            return;
          }
          if (!initialBuildTimeRef.current) {
            initialBuildTimeRef.current = remoteBuild;
          }
        }
      }

      // 2. Fallback via index.html (detecta mudança nos bundles compilados pelo Webpack/CRA)
      const hRes = await fetch(`/?_t=${Date.now()}`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache, no-store, must-revalidate", Pragma: "no-cache" }
      });

      if (hRes.ok) {
        const html = await hRes.text();
        const matches = html.match(/src=["']([^"']*static\/js\/main[^"']*)["']/g);
        if (matches && initialScriptsRef.current.size > 0) {
          let hasDiff = false;
          matches.forEach((m) => {
            const cleanSrc = m.replace(/src=["']/, "").replace(/["']$/, "");
            if (!initialScriptsRef.current.has(cleanSrc)) {
              hasDiff = true;
            }
          });
          if (hasDiff) {
            console.log("[UpdateNotifier] 🚀 Nova versão de bundle detectada no index.html!");
            setUpdateAvailable(true);
            return;
          }
        }
      }
    } catch (e) {
      // Falhas de rede silenciosas
    }
  }, [dismissed, updateAvailable]);

  // Checagem periódica (a cada 1 minuto) e ao refocar na aba do navegador
  useEffect(() => {
    // Intervalo de 1 minuto
    const interval = setInterval(checkForUpdates, 1 * 60 * 1000);

    // Checa ao retornar à aba
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdates();
      }
    };

    const handleFocus = () => checkForUpdates();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Expõe funções auxiliares para teste no console
    window.checkForUpdates = checkForUpdates;
    window.simulateAppUpdate = () => {
      setDismissed(false);
      setUpdateAvailable(true);
      console.log("[UpdateNotifier] Simulação de atualização ativada.");
    };

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      delete window.checkForUpdates;
      delete window.simulateAppUpdate;
    };
  }, [checkForUpdates]);

  // Dispara recarga forçada sem cache (Ctrl + F5 programático)
  const handleUpdateNow = async () => {
    setUpdating(true);
    try {
      // Limpa todas as caches registradas pelo navegador (ServiceWorker / Cache API)
      if ("caches" in window) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      }
    } catch (err) {
      console.warn("Erro ao limpar caches:", err);
    }

    // Força reload com timestamp na URL para quebrar caches de proxy/CDN/navegador
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("_v", Date.now().toString());
    window.location.replace(currentUrl.toString());
  };

  if (!updateAvailable || dismissed) {
    return null;
  }

  return (
    <Slide direction="up" in={updateAvailable && !dismissed} mountOnEnter unmountOnExit>
      <MDBox
        sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 99999,
          width: "92%",
          maxWidth: "540px"
        }}
      >
        <Card
          sx={{
            p: 2,
            background: darkMode ? "#383C48" : "#ffffff",
            border: darkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #e0e0e0",
            boxShadow: darkMode
              ? "0 14px 38px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4)"
              : "0 14px 38px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)",
            borderRadius: "14px"
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            {/* Ícone com destaque */}
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="44px"
              height="44px"
              minWidth="44px"
              borderRadius="12px"
              bgColor="warning"
              variant="gradient"
              shadow="md"
            >
              <Icon sx={{ color: "#fff !important", fontSize: "24px !important" }}>
                rocket_launch
              </Icon>
            </MDBox>

            {/* Texto informativo */}
            <MDBox flex={1}>
              <MDTypography
                variant="button"
                fontWeight="bold"
                color={darkMode ? "white" : "dark"}
                sx={{ display: "block", fontSize: "0.92rem", lineHeight: 1.2 }}
              >
                Nova versão disponível!
              </MDTypography>
              <MDTypography
                variant="caption"
                color={darkMode ? "text" : "secondary"}
                sx={{ display: "block", mt: 0.3, fontSize: "0.78rem" }}
              >
                Uma atualização do sistema foi publicada. Atualize agora para receber as novidades.
              </MDTypography>
            </MDBox>

            {/* Botão de Fechar */}
            <IconButton
              size="small"
              onClick={() => setDismissed(true)}
              sx={{ color: darkMode ? "#ffffff" : "#7b809a" }}
              title="Lembrar mais tarde"
            >
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </MDBox>

          {/* Barra de ações */}
          <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={1.5} mt={1.8}>
            <MDButton
              variant="text"
              color={darkMode ? "white" : "secondary"}
              size="small"
              onClick={() => setDismissed(true)}
              sx={{ fontSize: "0.78rem" }}
            >
              Lembrar mais tarde
            </MDButton>
            <MDButton
              variant="contained"
              color="warning"
              size="small"
              startIcon={<Icon>refresh</Icon>}
              onClick={handleUpdateNow}
              disabled={updating}
              sx={{
                px: 2.2,
                py: 0.8,
                fontSize: "0.82rem",
                fontWeight: "bold",
                boxShadow: "0 4px 12px rgba(234, 138, 0, 0.4)"
              }}
            >
              {updating ? "Atualizando..." : "Atualizar agora"}
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </Slide>
  );
}
